import { useQuery, useQueryClient } from '@tanstack/react-query';
import { swatidService } from '@/services';
import { sleep } from '@/utils/swatidValidation';
import type { SwatidCompareQueryParams, SwatidEventsQueryParams } from '@/types/api';

export function useSwatidStatus(controllerId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['swatid-status', controllerId],
    queryFn: async () => {
      const res = await swatidService.getStatus(controllerId);
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Error al obtener estado');
      }
      return { ...res.data, cached: res.cached ?? res.data.cached };
    },
    enabled: !!controllerId && enabled,
    refetchInterval: (query) => {
      const relays = query.state.data?.relays ?? [];
      const hasActive = relays.some((r) => r);
      return hasActive ? 5000 : false;
    },
  });
}

export function useSwatidInfo(controllerId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['swatid-info', controllerId],
    queryFn: async () => {
      const res = await swatidService.getInfo(controllerId);
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Error al obtener info');
      }
      return { ...res.data, cached: res.cached };
    },
    enabled: !!controllerId && enabled,
  });
}

export function useSwatidRelays(controllerId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['swatid-relays', controllerId],
    queryFn: async () => {
      const res = await swatidService.getRelays(controllerId);
      if (!res.success) throw new Error(res.error?.message || 'Error al obtener relés');
      return res.data ?? [];
    },
    enabled: !!controllerId && enabled,
    refetchInterval: (query) => {
      const relays = query.state.data ?? [];
      const hasTimer = relays.some((r) => r.timerRemaining > 0);
      return hasTimer ? 3000 : false;
    },
  });
}

export function useSwatidSchedules(controllerId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['swatid-schedules', controllerId],
    queryFn: async () => {
      const res = await swatidService.getSchedules(controllerId);
      if (!res.success) throw new Error(res.error?.message || 'Error al obtener programaciones');
      return res.data;
    },
    enabled: !!controllerId && enabled,
    refetchInterval: (query) => {
      const schedules = query.state.data?.schedules ?? [];
      const transitional = schedules.some((s) => {
        const v = s.verificationStatus;
        return v === 'pending' || v === 'sent' || v === 'confirmed';
      });
      return transitional ? 12_000 : false;
    },
  });
}

export function useSwatidRefresh(controllerId: string) {
  const queryClient = useQueryClient();

  const refreshAll = async (delayMs = 1000) => {
    if (delayMs > 0) await sleep(delayMs);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['swatid-status', controllerId] }),
      queryClient.invalidateQueries({ queryKey: ['swatid-info', controllerId] }),
      queryClient.invalidateQueries({ queryKey: ['swatid-relays', controllerId] }),
      queryClient.invalidateQueries({ queryKey: ['swatid-schedules', controllerId] }),
      queryClient.invalidateQueries({ queryKey: ['swatid-events', controllerId] }),
      queryClient.invalidateQueries({ queryKey: ['swatid-compare', controllerId] }),
      queryClient.invalidateQueries({ queryKey: ['swatid-supervision', controllerId] }),
      queryClient.invalidateQueries({ queryKey: ['schedule-verification', controllerId] }),
      queryClient.invalidateQueries({ queryKey: ['controllers'] }),
    ]);
  };

  const refreshStatus = async () => {
    await queryClient.invalidateQueries({ queryKey: ['swatid-status', controllerId] });
    await queryClient.invalidateQueries({ queryKey: ['swatid-info', controllerId] });
    await queryClient.invalidateQueries({ queryKey: ['controllers'] });
  };

  return { refreshAll, refreshStatus };
}

export function useSwatidEvents(
  controllerId: string,
  params: SwatidEventsQueryParams,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['swatid-events', controllerId, params],
    queryFn: async () => {
      const res = await swatidService.getEvents(controllerId, params);
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Error al obtener eventos');
      }
      return res.data;
    },
    enabled: !!controllerId && enabled,
    refetchInterval: 60_000,
  });
}

export function useSwatidRelayHistory(
  controllerId: string,
  relayNumber: number,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['swatid-relay-history', controllerId, relayNumber],
    queryFn: async () => {
      const res = await swatidService.getRelayHistory(controllerId, relayNumber);
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Error al obtener historial');
      }
      return res.data;
    },
    enabled: !!controllerId && enabled && relayNumber >= 1,
  });
}

export function useSwatidEventsCompare(
  controllerId: string,
  params: SwatidCompareQueryParams,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['swatid-compare', controllerId, params],
    queryFn: async () => {
      const res = await swatidService.compareEvents(controllerId, params);
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Error al comparar eventos');
      }
      return res.data;
    },
    enabled: !!controllerId && enabled,
  });
}

export function useSwatidSupervision(controllerId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['swatid-supervision', controllerId],
    queryFn: async () => {
      const res = await swatidService.getSupervision(controllerId);
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Error al obtener supervisión');
      }
      return res.data;
    },
    enabled: !!controllerId && enabled,
    refetchInterval: 60_000,
  });
}
